import React from 'react';
import PropTypes from 'prop-types';
import TextInput from 'relements/build/esm/input/text';
import Text from 'relements/build/esm/ui/text';
import Checkbox from 'relements/build/esm/input/checkbox';
import Tabs from 'relements/build/esm/ui/tabs';

// External

import { generateNodeName } from '@src/utils/NodeUtils';
import {
  NODE_TYPES,
  CONTEXT_CLEAR_NODE,
  END_OF_QUERY,
  DEFAULT_LANGUAGE,
} from '@src/constants/MogamboConstants';
import UserResponseSynonymTable from '@src/components/UserResponseSynonymTable';
import BotSaysCard from '@src/components/BotResponses/BotSays/BotSaysCard';
import { isNewPipeline as checkIsNewPipeline } from '@src/actions/_utils';
import BotStore from '@src/store';
import CommonStore from '@src/stores/CommonStore';

import './GeneralNodeItem.scss';
export default class GeneralNodeItem extends React.Component {
  constructor() {
    super();

    this.allLanguages = CommonStore.getMetaTags()?.allLanguages;
    this.hybridLanguages = CommonStore.getAllHybridLanguages() || [];

    this.state = {
      currentLanguageOfNode: DEFAULT_LANGUAGE,
      currentScript: this.hybridLanguages[DEFAULT_LANGUAGE]?.[0]?.script || '',
    };
  }

  render() {
    const transposedUserResponses = this.props.user_responses.map(
      (userResponse) => ({
        id: userResponse.id,
        active: userResponse.active,
        title: userResponse.sentence,
        tags: userResponse.keywords,
        is_system_generated: userResponse.is_system_generated,
        is_typing_suggestion: userResponse.is_typing_suggestion,
        is_disambiguation_suggestion: userResponse.is_disambiguation_suggestion,
      })
    );

    return (
      <div className="general-nodes-overlay-item-body">
        {this.renderHeader()}
        <div className="general-nodes-overlay-item-responses">
          {this.renderUserSays(transposedUserResponses)}
          {this.renderBotSays()}
        </div>
      </div>
    );
  }

  renderHeader = () => {
    const nodeContextMenuEnabled = BotStore.getEnableNodeContextMenuFlag();
    return (
      <header className="general-nodes-overlay-item-body-header">
        <TextInput
          className="general-nodes-overlay-item-body-header-input"
          id={`${this.props.id}|title`}
          label="Step Name"
          type="text"
          value={this.props.title}
          placeholder="Type step name"
          updateValue={this._onUpdate}
          themed
          onChange={(e) =>
            this._onUpdate({
              id: `${this.props.id}|title|`,
              value: e,
            })
          }
        />

        <Checkbox.Item
          label={`Mark as ${
            nodeContextMenuEnabled ? 'End of Query' : 'Context Clear'
          } Step?`}
          className="general-nodes-overlay-item-body-header-checkbox"
          value={
            nodeContextMenuEnabled
              ? this.props[END_OF_QUERY.KEY]
              : this.props[CONTEXT_CLEAR_NODE.KEY]
          }
          onChange={(e) =>
            this._onUpdate({
              id: `${this.props.id}|${
                nodeContextMenuEnabled
                  ? END_OF_QUERY.KEY
                  : CONTEXT_CLEAR_NODE.KEY
              }|`,
              value: e,
            })
          }
        />
      </header>
    );
  };

  renderUserSays = (transposedUserResponses) => (
    <div className="general-nodes-overlay-item-user-says">
      <Text
        className="general-nodes-overlay-item-user-says-title"
        variant="h3.semi-bold"
      >
        User Messages
      </Text>
      <UserResponseSynonymTable
        context={{
          connectingNode: 'default',
          nodeId: this.props.node_id,
          domainId: this.props.domain.id,
          domainTitle: this.props.domain.title,
          isNewPipeline: checkIsNewPipeline(this.props.domain),
        }}
        screen="default"
        innerdata={{ default: transposedUserResponses }}
        editedData={{}}
        contextMenuOffset={{ y: 160, x: -30 }}
        synonyms={this.props.synonyms}
        updatedSynonyms={this.props.updatedSynonyms}
        meanings={this.props.meanings}
        noAutoFocus
        updateInputs={(e) => {
          this._onUpdate({
            id: `${this.props.id}|user_responses|`,
            ...e,
          });
        }}
        style={{ paddingTop: 20 }}
        onFAQNode
      />
    </div>
  );

  updateLanguage(selectedLanguage) {
    this.setState({
      currentLanguageOfNode: selectedLanguage,
      currentScript: this.hybridLanguages[selectedLanguage]?.[0]?.script,
    });
    this.props.updateCurrentSourceLanguage(selectedLanguage);
  }

  getLanguageTitle = (languageCode) => {
    let languageTitle = '';
    if (!this.allLanguages) return languageTitle;
    this.allLanguages.forEach((language) => {
      if (language.code === languageCode) {
        languageTitle = language.name;
      }
    });
    return languageTitle;
  };

  handleHindiScriptChange = (script) => {
    this.setState({
      currentScript: script,
    });
  };

  renderBotSays = () => {
    return (
      <div className="general-nodes-overlay-item-bot-says">
        <BotSaysCard
          title="Bot Responses"
          type="DEFAULT"
          openCard
          faq_ww_header={
            this.props.supportedLanguages.length > 1 && (
              <>
                <Tabs
                  className="botSaysLanguageTabs"
                  value={this.state.currentLanguageOfNode}
                >
                  {this.props.supportedLanguages.map((tab) => (
                    <Tabs.Item
                      key={tab}
                      value={tab}
                      onClick={() => {
                        this.updateLanguage(tab);
                      }}
                    >
                      <Text variant="h4.semi-bold">
                        {tab === 'hi' ? 'Hindi' : this.getLanguageTitle(tab)}
                      </Text>
                    </Tabs.Item>
                  ))}
                </Tabs>
                <>
                  {this.hybridLanguages?.[this.state.currentLanguageOfNode]
                    .length > 1 && (
                    <Tabs
                      className="hindiScriptTabs hindiScriptTabsActive"
                      value={this.state.currentScript}
                    >
                      {this.hybridLanguages[
                        this.state.currentLanguageOfNode
                      ].map((tab) => (
                        <Tabs.Item
                          key={tab.script}
                          value={tab.script}
                          onClick={this.handleHindiScriptChange}
                        >
                          <Text variant="h4.semi-bold">{tab.native_name}</Text>
                        </Tabs.Item>
                      ))}
                    </Tabs>
                  )}
                </>
              </>
            )
          }
          value={{
            variants: this.props.responses[this.state.currentLanguageOfNode]
              ? this.props.responses[this.state.currentLanguageOfNode]?.[
                  this.state.currentScript
                ]?.bot_responses || []
              : [],
            quickReplies: this.props.responses[this.state.currentLanguageOfNode]
              ? this.props.responses[this.state.currentLanguageOfNode]?.[
                  this.state.currentScript
                ]?.quick_replies || []
              : [],
          }}
          currentBotSaysLanguage={this.state.currentLanguageOfNode}
          hideHeader
          onChange={this._handleChange}
        />
      </div>
    );
  };

  _handleChange = (lowerKey, value) => {
    this.props.updateCurrentSourceLanguage(this.state.currentLanguageOfNode);
    lowerKey = lowerKey === 'variants' ? 'bot_responses' : 'quick_replies';
    this.props.dataUpdateCallback(
      this.props.node_id,
      lowerKey,
      value,
      this.state.currentLanguageOfNode,
      this.state.currentScript
    );
  };

  _onUpdate = (data) => {
    const splitData = data.id.split('|');
    switch (splitData[1]) {
      case 'title':
        this.props.dataUpdateCallback(this.props.node_id, 'title', data.value);
        if (this.props.new_node) {
          const domainName = this.props.domain.title;
          const storyName = this.props.story ? this.props.story.title : null;
          let systemName;
          if (storyName) {
            // Story General Node
            systemName = generateNodeName(
              NODE_TYPES.STORY_GENERAL,
              data.value,
              domainName,
              storyName
            );
          } else {
            // Bot General Node
            systemName = generateNodeName(
              NODE_TYPES.BOT_GENERAL,
              data.value,
              domainName
            );
          }
          this.props.dataUpdateCallback(
            this.props.node_id,
            'system_name',
            systemName
          );
        }
        break;

      case 'entity':
        this.props.dataUpdateCallback(this.props.node_id, 'entity', data.value);
        break;

      case 'user_responses': {
        const updatedResponses = data.default.map((userResponse) => ({
          id: userResponse.id,
          active: userResponse.active,
          sentence: userResponse.title,
          keywords: userResponse.tags,
          is_system_generated: userResponse.is_system_generated,
          is_typing_suggestion: userResponse.is_typing_suggestion,
          is_disambiguation_suggestion:
            userResponse.is_disambiguation_suggestion,
        }));
        this.props.dataUpdateCallback(
          this.props.node_id,
          'user_responses',
          updatedResponses
        );
        break;
      }

      case 'bot_responses':
        this.props.dataUpdateCallback(
          this.props.node_id,
          'bot_responses',
          data.botResponses
        );
        break;

      case CONTEXT_CLEAR_NODE.KEY:
        this.props.dataUpdateCallback(
          this.props.node_id,
          CONTEXT_CLEAR_NODE.KEY,
          data.value
        );
        break;

      case END_OF_QUERY.KEY:
        this.props.dataUpdateCallback(
          this.props.node_id,
          END_OF_QUERY.KEY,
          data.value
        );
        break;

      default:
        break;
    }
  };
}

GeneralNodeItem.propTypes = {
  active: PropTypes.bool,
  quick_replies: PropTypes.arrayOf(PropTypes.string),
  synonyms: PropTypes.object,
  updatedSynonyms: PropTypes.object,
  currentLanguage: PropTypes.string,
  meanings: PropTypes.object,
  domain: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  story: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
  }),
  context_clear: PropTypes.bool,
  system_name: PropTypes.string,
  title: PropTypes.string,
  user_responses: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      active: PropTypes.bool,
      title: PropTypes.string,
      tags: PropTypes.arrayOf(PropTypes.string),
      is_system_generated: PropTypes.bool,
    })
  ),
  dataUpdateCallback: PropTypes.func,
  bot_responses: PropTypes.arrayOf(PropTypes.string),
  responses: PropTypes.shape({}),
  id: PropTypes.string,
  node_id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  new_node: PropTypes.bool,
};
